import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";

/**
 * Fetches real marine + atmospheric weather from Open-Meteo (free, no key).
 * Marine API: https://marine-api.open-meteo.com
 * Forecast API: https://api.open-meteo.com
 */
export function useMarineWeather(lat, lng) {
  return useQuery({
    queryKey: ["marine-weather", lat?.toFixed(3), lng?.toFixed(3)],
    queryFn: async () => {
      try {
        const response = await base44.functions.invoke("getMarineWeatherData", {
          latitude: lat,
          longitude: lng,
        });

        return response.data;
      } catch (error) {
        return {
          marine: null,
          forecast: null,
          requested_location: { latitude: lat, longitude: lng },
          is_fallback: true,
          fallback_reason: error?.response?.data?.error || error?.message || "Weather temporarily unavailable",
        };
      }
    },
    enabled: !!lat && !!lng,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
  });
}

/** Converts wind direction degrees to compass bearing string */
export function degreesToCompass(deg) {
  if (deg == null) return "—";
  const dirs = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
  return dirs[Math.round(deg / 22.5) % 16];
}

/** Beaufort scale from knots */
export function beaufortFromKnots(knots) {
  if (knots == null) return { force: "—", desc: "—" };
  const scale = [
    { max: 1,   force: 0, desc: "Calm" },
    { max: 3,   force: 1, desc: "Light air" },
    { max: 6,   force: 2, desc: "Light breeze" },
    { max: 10,  force: 3, desc: "Gentle breeze" },
    { max: 16,  force: 4, desc: "Moderate breeze" },
    { max: 21,  force: 5, desc: "Fresh breeze" },
    { max: 27,  force: 6, desc: "Strong breeze" },
    { max: 33,  force: 7, desc: "Near gale" },
    { max: 40,  force: 8, desc: "Gale" },
    { max: 47,  force: 9, desc: "Strong gale" },
    { max: 55,  force: 10, desc: "Storm" },
    { max: 63,  force: 11, desc: "Violent storm" },
    { max: Infinity, force: 12, desc: "Hurricane" },
  ];
  const b = scale.find(s => knots <= s.max);
  return b || { force: 12, desc: "Hurricane" };
}

/** Sailing safety assessment */
export function sailingSafety(windKnots, waveM) {
  if (windKnots >= 40 || waveM >= 4) return { level: "danger",  label: "Dangerous",  color: "text-red-500",    bg: "bg-red-500/10",    border: "border-red-500/30" };
  if (windKnots >= 25 || waveM >= 2.5) return { level: "warning", label: "Caution",    color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/30" };
  if (windKnots >= 15 || waveM >= 1.5) return { level: "moderate",label: "Moderate",   color: "text-amber-500",  bg: "bg-amber-500/10",  border: "border-amber-500/30" };
  return { level: "safe",    label: "Good",        color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/30" };
}

/** Build hourly chart data from API response */
export function buildHourlyData(marine, forecast, hours = 24) {
  const result = [];
  const times = forecast?.hourly?.time || [];
  const now = Date.now();
  const startIndex = Math.max(0, times.findIndex((time) => new Date(time).getTime() >= now - 60 * 60 * 1000));
  for (let i = startIndex; i < startIndex + hours; i++) {
    const time = forecast?.hourly?.time?.[i];
    if (!time) continue;
    const label = new Date(time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    result.push({
      time: label,
      windSpeed:   Math.round(forecast?.hourly?.wind_speed_10m?.[i] ?? 0),
      windGusts:   Math.round(forecast?.hourly?.wind_gusts_10m?.[i] ?? 0),
      waveHeight:  +(marine?.hourly?.wave_height?.[i] ?? 0).toFixed(2),
      swellHeight: +(marine?.hourly?.swell_wave_height?.[i] ?? 0).toFixed(2),
      temp:        Math.round(forecast?.hourly?.temperature_2m?.[i] ?? 0),
      pressure:    Math.round(forecast?.hourly?.pressure_msl?.[i] ?? 0),
      humidity:    Math.round(forecast?.hourly?.relative_humidity_2m?.[i] ?? 0),
      precipitation: Math.round(forecast?.hourly?.precipitation_probability?.[i] ?? 0),
    });
  }
  return result;
}